import { useState, useEffect, useMemo } from 'react';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  getListaSpesa, addToListaSpesa, marcaPreso, annullaPreso, archiviaPresi,
  deleteListaSpesaItem, svuotaArchivioListaSpesa, getArticoli, getCategorieArticoli,
  getFornitori
} from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Plus, Trash2, ShoppingCart, CheckCircle2, Archive, RotateCcw,
  Clock, User, Tag as TagIcon, Phone, ShoppingBag, MessageCircle, ExternalLink
} from 'lucide-react';

const SENZA_CAT = '__senza__';
const TUTTE = 'all';

// ============== WHATSAPP HELPERS ==============
const buildWhatsAppLink = (telefono, fornitoreNome, items) => {
  let phone = (telefono || '').replace(/\D/g, '');
  if (!phone.startsWith('39')) phone = '39' + phone;
  let msg = '🛒 *Richiesta articoli*\n\n';
  msg += `Ciao ${fornitoreNome},\n\nci servirebbero:\n\n`;
  items.forEach((it) => {
    msg += `• ${it.articolo_nome}: ${it.quantita} ${it.unita_misura || 'pz'}`;
    if (it.note) msg += ` _(${it.note})_`;
    msg += '\n';
  });
  msg += '\nGrazie!';
  return `https://wa.me/${phone}?text=${encodeURIComponent(msg)}`;
};

// ============== FORM AGGIUNTA ARTICOLO ==============
const AggiungiArticoloDialog = ({ articoli, categorie, onAdd, onClose, open, setOpen }) => {
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState(TUTTE);
  const [selectedId, setSelectedId] = useState('');
  const [quantita, setQuantita] = useState(1);
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  const filtered = useMemo(() => {
    let list = articoli;
    if (filterCat !== TUTTE) {
      list = list.filter((a) => (filterCat === SENZA_CAT ? !a.categoria : a.categoria === filterCat));
    }
    if (search.trim()) {
      const q = search.toLowerCase().trim();
      list = list.filter((a) => a.nome.toLowerCase().includes(q));
    }
    return list.slice(0, 100); // limita risultati per performance
  }, [articoli, filterCat, search]);

  const reset = () => {
    setSearch('');
    setFilterCat(TUTTE);
    setSelectedId('');
    setQuantita(1);
    setNote('');
  };

  const handleSubmit = async () => {
    if (!selectedId) {
      toast.error('Seleziona un articolo');
      return;
    }
    if (quantita < 1) {
      toast.error('La quantità deve essere almeno 1');
      return;
    }
    setLoading(true);
    try {
      await onAdd({ articolo_id: selectedId, quantita: parseInt(quantita, 10), note: note || null });
      reset();
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore aggiunta');
    } finally {
      setLoading(false);
    }
  };

  const selectedArticolo = articoli.find((a) => a.id === selectedId);

  return (
    <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) reset(); }}>
      <DialogTrigger asChild>
        <Button data-testid="add-lista-btn">
          <Plus className="h-4 w-4 mr-2" />
          Aggiungi alla Lista
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-[600px] max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Aggiungi articolo alla lista spesa</DialogTitle>
          <DialogDescription>
            Cerca un articolo dal catalogo. Se manca, chiedi all'admin di aggiungerlo prima.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="flex gap-2">
            <Input
              placeholder="Cerca articolo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="flex-1"
              data-testid="search-articolo-input"
            />
            <Select value={filterCat} onValueChange={setFilterCat}>
              <SelectTrigger className="w-[180px]" data-testid="filter-cat-add">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={TUTTE}>Tutte categorie</SelectItem>
                <SelectItem value={SENZA_CAT}>Senza categoria</SelectItem>
                {categorie.map((c) => (
                  <SelectItem key={c.id} value={c.nome}>{c.nome}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="max-h-[300px] overflow-y-auto border rounded-lg">
            {filtered.length === 0 ? (
              <div className="p-6 text-center text-muted-foreground text-sm">
                Nessun articolo trovato. {search && 'Prova a cambiare la ricerca.'}
              </div>
            ) : (
              filtered.map((a) => (
                <button
                  key={a.id}
                  type="button"
                  onClick={() => setSelectedId(a.id)}
                  className={`w-full text-left p-3 border-b last:border-b-0 hover:bg-secondary/40 transition-colors ${
                    selectedId === a.id ? 'bg-primary/10 border-l-4 border-l-primary' : ''
                  }`}
                  data-testid={`articolo-option-${a.id}`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{a.nome}</p>
                      <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                        {a.categoria && <Badge variant="secondary" className="text-xs">{a.categoria}</Badge>}
                        {a.fornitore_nome && <span className="truncate">{a.fornitore_nome}</span>}
                      </div>
                    </div>
                    <span className="text-xs text-muted-foreground whitespace-nowrap">{a.unita_misura || 'pz'}</span>
                  </div>
                </button>
              ))
            )}
          </div>

          {selectedArticolo && (
            <Card className="bg-primary/5 border-primary/30">
              <CardContent className="p-4 space-y-3">
                <div>
                  <p className="text-xs text-muted-foreground">Articolo selezionato:</p>
                  <p className="font-semibold">{selectedArticolo.nome}</p>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label htmlFor="quantita">Quantità *</Label>
                    <Input
                      id="quantita"
                      type="number"
                      min="1"
                      value={quantita}
                      onChange={(e) => setQuantita(e.target.value)}
                      data-testid="quantita-input"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label htmlFor="note">Note (opzionale)</Label>
                  <Textarea
                    id="note"
                    placeholder="Es. marca specifica, urgente..."
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    rows={2}
                    data-testid="note-input"
                  />
                </div>
              </CardContent>
            </Card>
          )}
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <Button variant="outline" onClick={() => { reset(); setOpen(false); }}>Annulla</Button>
          <Button onClick={handleSubmit} disabled={loading || !selectedId} data-testid="submit-lista-btn">
            {loading ? 'Aggiunta...' : 'Aggiungi'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// ============== UTILITY ==============
const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleString('it-IT', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
};

const groupByCategoria = (items) => {
  const groups = {};
  items.forEach((item) => {
    const cat = item.categoria || 'Senza categoria';
    if (!groups[cat]) groups[cat] = [];
    groups[cat].push(item);
  });
  return Object.entries(groups).sort(([a], [b]) => {
    if (a === 'Senza categoria') return 1;
    if (b === 'Senza categoria') return -1;
    return a.localeCompare(b);
  });
};

// ============== ITEM CARD ==============
const ItemCard = ({ item, mode, onPreso, onAnnullaPreso, onDelete, currentUserId, isAdmin }) => {
  const canDelete = isAdmin || item.inserito_da_id === currentUserId;
  const isPreso = item.stato === 'preso';
  const isArch = item.stato === 'archiviato';

  return (
    <div
      className={`flex items-start gap-3 p-3 border rounded-lg transition-colors ${
        isPreso ? 'bg-emerald-500/5 border-emerald-500/30' : isArch ? 'bg-secondary/30 opacity-75' : 'bg-card hover:bg-secondary/30'
      }`}
      data-testid={`lista-item-${item.id}`}
    >
      {mode === 'da_comprare' && (
        <Checkbox
          checked={false}
          onCheckedChange={() => onPreso(item.id)}
          className="mt-1"
          data-testid={`check-item-${item.id}`}
        />
      )}
      {mode === 'preso' && (
        <Checkbox
          checked={true}
          onCheckedChange={() => onAnnullaPreso(item.id)}
          className="mt-1"
          data-testid={`uncheck-item-${item.id}`}
        />
      )}
      {mode === 'archiviato' && (
        <CheckCircle2 className="h-5 w-5 text-muted-foreground mt-1 flex-shrink-0" />
      )}

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-2 flex-wrap">
          <p className={`font-medium ${isPreso || isArch ? 'line-through text-muted-foreground' : ''}`}>
            {item.articolo_nome}
          </p>
          <Badge variant="outline" className="font-mono">
            {item.quantita} {item.unita_misura || 'pz'}
          </Badge>
        </div>

        {item.note && (
          <p className="text-sm text-muted-foreground mt-1 italic">"{item.note}"</p>
        )}

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground mt-2">
          {item.fornitore_nome && (
            <span className="flex items-center gap-1">
              <Phone className="h-3 w-3" />
              {item.fornitore_nome}
            </span>
          )}
          <span className="flex items-center gap-1">
            <User className="h-3 w-3" />
            Agg. da <strong className="text-foreground/80">{item.inserito_da_nome}</strong>
          </span>
          <span className="flex items-center gap-1">
            <Clock className="h-3 w-3" />
            {formatDate(item.inserito_at)}
          </span>
        </div>

        {(isPreso || isArch) && item.preso_at && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-emerald-600 dark:text-emerald-400 mt-1">
            <span className="flex items-center gap-1">
              <CheckCircle2 className="h-3 w-3" />
              Preso da <strong>{item.preso_da_nome}</strong>
            </span>
            <span className="flex items-center gap-1">
              <Clock className="h-3 w-3" />
              {formatDate(item.preso_at)}
            </span>
          </div>
        )}

        {isArch && item.archiviato_at && (
          <div className="flex items-center gap-1 text-xs text-muted-foreground mt-1">
            <Archive className="h-3 w-3" />
            Archiviato il {formatDate(item.archiviato_at)}
          </div>
        )}
      </div>

      {canDelete && mode !== 'archiviato' && (
        <Button
          variant="ghost"
          size="icon"
          className="text-destructive hover:text-destructive flex-shrink-0"
          onClick={() => onDelete(item.id)}
          data-testid={`delete-item-${item.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
};

// ============== PAGINA ==============
const ListaSpesa = () => {
  const [items, setItems] = useState([]);
  const [articoli, setArticoli] = useState([]);
  const [categorie, setCategorie] = useState([]);
  const [fornitori, setFornitori] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('da_comprare');
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  useEffect(() => {
    loadAll();
  }, []);

  const loadAll = async () => {
    try {
      const [itemsData, artData, catData, fornData] = await Promise.all([
        getListaSpesa(),
        getArticoli(),
        getCategorieArticoli(),
        getFornitori(),
      ]);
      setItems(itemsData);
      setArticoli(artData);
      setCategorie(catData);
      setFornitori(fornData);
    } catch (error) {
      toast.error('Errore caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const reloadItems = async () => {
    try {
      const data = await getListaSpesa();
      setItems(data);
    } catch (e) { /* ignore */ }
  };

  const handleAdd = async (data) => {
    await addToListaSpesa(data);
    toast.success('Aggiunto alla lista');
    reloadItems();
  };

  const handlePreso = async (id) => {
    try {
      await marcaPreso(id);
      toast.success('Segnato come preso');
      reloadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleAnnullaPreso = async (id) => {
    try {
      await annullaPreso(id);
      toast.success('Rimesso in lista');
      reloadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Rimuovere questo articolo dalla lista?')) return;
    try {
      await deleteListaSpesaItem(id);
      toast.success('Rimosso');
      reloadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore');
    }
  };

  const handleArchiviaPresi = async () => {
    if (!window.confirm('Spostare tutti gli articoli "presi" nell\'archivio?')) return;
    try {
      const result = await archiviaPresi();
      toast.success(result.message);
      reloadItems();
      setActiveTab('da_comprare');
    } catch (error) {
      toast.error('Errore archiviazione');
    }
  };

  const handleSvuotaArchivio = async () => {
    if (!window.confirm('Eliminare DEFINITIVAMENTE tutti gli articoli archiviati? Questa azione non è reversibile.')) return;
    try {
      const result = await svuotaArchivioListaSpesa();
      toast.success(result.message);
      reloadItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore svuotamento');
    }
  };

  const daComprare = items.filter((i) => i.stato === 'da_comprare');
  const presi = items.filter((i) => i.stato === 'preso');
  const archiviati = items.filter((i) => i.stato === 'archiviato');

  // Raggruppa da_comprare per fornitore (con telefono) per generare link WhatsApp
  const whatsappGroups = useMemo(() => {
    const groups = {};
    daComprare.forEach((it) => {
      if (!it.fornitore_id) return;
      const forn = fornitori.find((f) => f.id === it.fornitore_id);
      if (!forn || !forn.telefono) return;
      if (!groups[it.fornitore_id]) {
        groups[it.fornitore_id] = { fornitore: forn, items: [] };
      }
      groups[it.fornitore_id].items.push(it);
    });
    return Object.values(groups);
  }, [daComprare, fornitori]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  const renderItems = (list, mode) => {
    if (list.length === 0) {
      return (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12 text-center">
            {mode === 'da_comprare' && <ShoppingBag className="h-12 w-12 text-muted-foreground mb-3" />}
            {mode === 'preso' && <CheckCircle2 className="h-12 w-12 text-muted-foreground mb-3" />}
            {mode === 'archiviato' && <Archive className="h-12 w-12 text-muted-foreground mb-3" />}
            <p className="text-muted-foreground">
              {mode === 'da_comprare' && 'Nessun articolo in lista. Aggiungi il primo!'}
              {mode === 'preso' && 'Nessun articolo ancora preso.'}
              {mode === 'archiviato' && 'Nessun articolo archiviato.'}
            </p>
          </CardContent>
        </Card>
      );
    }

    const groups = groupByCategoria(list);
    return (
      <div className="space-y-4">
        {groups.map(([cat, groupItems]) => (
          <div key={cat} className="space-y-2">
            <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground">
              <TagIcon className="h-4 w-4" />
              {cat}
              <Badge variant="outline" className="text-xs">{groupItems.length}</Badge>
            </div>
            <div className="space-y-2">
              {groupItems.map((item) => (
                <ItemCard
                  key={item.id}
                  item={item}
                  mode={mode}
                  onPreso={handlePreso}
                  onAnnullaPreso={handleAnnullaPreso}
                  onDelete={handleDelete}
                  currentUserId={user?.id}
                  isAdmin={isAdmin}
                />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="space-y-6 animate-fadeIn" data-testid="lista-spesa-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <ShoppingCart className="h-7 w-7" />
            Lista Spesa
          </h1>
          <p className="text-muted-foreground mt-1">
            Lista condivisa: aggiungi cosa serve, spunta cosa hai preso.
          </p>
        </div>
        <AggiungiArticoloDialog
          articoli={articoli}
          categorie={categorie}
          onAdd={handleAdd}
          open={dialogOpen}
          setOpen={setDialogOpen}
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-4">
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="da_comprare" data-testid="tab-da-comprare">
            <ShoppingBag className="h-4 w-4 mr-2" />
            Da comprare ({daComprare.length})
          </TabsTrigger>
          <TabsTrigger value="preso" data-testid="tab-preso">
            <CheckCircle2 className="h-4 w-4 mr-2" />
            Presi ({presi.length})
          </TabsTrigger>
          <TabsTrigger value="archiviato" data-testid="tab-archiviato">
            <Archive className="h-4 w-4 mr-2" />
            Archivio ({archiviati.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="da_comprare" className="space-y-4">
          {whatsappGroups.length > 0 && (
            <Card className="border-green-600/30 bg-green-600/5">
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <MessageCircle className="h-5 w-5 text-green-600" />
                  <h3 className="font-semibold">Invia richieste WhatsApp ({whatsappGroups.length} fornitori)</h3>
                </div>
                <p className="text-xs text-muted-foreground mb-3">
                  Genera un messaggio WhatsApp pronto per ogni fornitore con i suoi articoli da comprare.
                </p>
                <div className="flex flex-wrap gap-2">
                  {whatsappGroups.map(({ fornitore, items: groupItems }) => {
                    const link = buildWhatsAppLink(fornitore.telefono, fornitore.nome, groupItems);
                    return (
                      <Button
                        key={fornitore.id}
                        variant="outline"
                        className="bg-green-600/10 border-green-600/50 hover:bg-green-600/20"
                        onClick={() => window.open(link, '_blank')}
                        data-testid={`whatsapp-fornitore-${fornitore.id}`}
                      >
                        <MessageCircle className="h-4 w-4 mr-2 text-green-600" />
                        {fornitore.nome}
                        <Badge variant="secondary" className="ml-2">{groupItems.length}</Badge>
                        <ExternalLink className="h-3 w-3 ml-1.5" />
                      </Button>
                    );
                  })}
                </div>
              </CardContent>
            </Card>
          )}
          {renderItems(daComprare, 'da_comprare')}
        </TabsContent>

        <TabsContent value="preso" className="space-y-4">
          {presi.length > 0 && (
            <div className="flex justify-end">
              <Button variant="outline" onClick={handleArchiviaPresi} data-testid="archivia-presi-btn">
                <Archive className="h-4 w-4 mr-2" />
                Archivia tutti i presi
              </Button>
            </div>
          )}
          {renderItems(presi, 'preso')}
        </TabsContent>

        <TabsContent value="archiviato" className="space-y-4">
          {isAdmin && archiviati.length > 0 && (
            <div className="flex justify-end">
              <Button variant="outline" className="text-destructive border-destructive/40 hover:bg-destructive/10"
                onClick={handleSvuotaArchivio} data-testid="svuota-archivio-btn">
                <Trash2 className="h-4 w-4 mr-2" />
                Svuota archivio
              </Button>
            </div>
          )}
          {renderItems(archiviati, 'archiviato')}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ListaSpesa;
